// Settings panel for per-org Usage rollups (M010) + Limits form.
// Reads GET /orgs/:id/usage and /orgs/:id/limits; writes limits via
// PUT. Pure presentational component — data and reload are owned
// inside so the parent only needs to hand in orgId.

import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  getOrgLimits,
  getOrgUsage,
  getWorkspaceUsage,
  setOrgLimits,
  type LimitsResponse,
  type UsageResponse,
} from "../api/client";

const DEFAULT_LIMITS: LimitsResponse = {
  per_turn_max_tokens: 2048,
  daily_cost_cap_micros: 5_000_000,
  monthly_cost_cap_micros: 100_000_000,
  soft_limit: true,
};

interface Props {
  orgId: number;
  workspaceId?: number;
  scopeLabel?: string;
}

function formatUsd(micros: number): string {
  const v = micros / 1_000_000;
  if (v === 0) return "$0";
  if (v < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}K`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

function shortDay(dayMicros: number): string {
  return new Date(dayMicros / 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function UsageLimitsPanel({
  orgId,
  workspaceId,
  scopeLabel = "this org",
}: Props) {
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [limits, setLimits] = useState<LimitsResponse | null>(null);
  const [draft, setDraft] = useState<LimitsResponse | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const usagePromise =
          workspaceId == null ? getOrgUsage(orgId) : getWorkspaceUsage(workspaceId);
        const [u, l] = await Promise.all([usagePromise, getOrgLimits(orgId)]);
        if (cancelled) return;
        setUsage(u);
        setLimits(l);
        setDraft(l);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : "Could not load usage / limits",
          );
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgId, workspaceId]);

  const dirty = useMemo(() => {
    if (!draft || !limits) return false;
    return (
      draft.per_turn_max_tokens !== limits.per_turn_max_tokens ||
      draft.daily_cost_cap_micros !== limits.daily_cost_cap_micros ||
      draft.monthly_cost_cap_micros !== limits.monthly_cost_cap_micros ||
      draft.soft_limit !== limits.soft_limit
    );
  }, [draft, limits]);

  async function saveLimits(e: React.FormEvent) {
    e.preventDefault();
    if (!draft || !dirty) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await setOrgLimits(orgId, draft);
      setLimits(draft);
      setNotice("Limits saved.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save limits");
    } finally {
      setBusy(false);
    }
  }

  function resetToDefaults() {
    if (!draft) return;
    setDraft(DEFAULT_LIMITS);
  }

  const maxDaily =
    usage?.daily_breakdown.reduce((m, d) => Math.max(m, d.cost_usd_micros), 1) ?? 1;
  const firstDay = usage?.daily_breakdown[0];
  const lastDay = usage?.daily_breakdown[usage.daily_breakdown.length - 1];

  return (
    <>
      <section
        className="flex flex-col gap-4 border-t border-edge pt-6"
        data-testid="usage-section"
      >
        <div>
          <h2 className="text-sm font-semibold text-primary">Usage</h2>
          <p className="mt-1 text-xs text-muted">
            Model, runtime, and email usage for {scopeLabel}.
          </p>
        </div>

        {usage === null ? (
          <p className="text-xs text-muted">Loading usage…</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-2" data-testid="usage-windows">
              {(["today", "week", "month"] as const).map((key) => {
                const w = usage[key];
                const label =
                  key === "today" ? "Today" : key === "week" ? "7 days" : "30 days";
                const noActivity =
                  w.turns === 0 &&
                  (w.remote_env_minutes ?? 0) === 0 &&
                  (w.resend_messages ?? 0) === 0;
                const totalCost =
                  w.cost_usd_micros + (w.remote_env_cost_usd_micros ?? 0);
                return (
                  <div
                    key={key}
                    className={`flex flex-col gap-1.5 rounded-md border border-edge bg-surface-raised/70 px-3 py-2 ${noActivity ? "opacity-70" : ""}`}
                    data-testid={`usage-${key}`}
                  >
                    <span className="text-xs text-faint">{label}</span>
                    <span className="text-lg font-semibold text-primary">
                      {formatUsd(totalCost)}
                    </span>
                    {noActivity ? (
                      <span className="text-xs text-muted">no activity</span>
                    ) : (
                      <div className="flex flex-col gap-0.5 text-xs">
                        <span className="text-muted">
                          <span className="font-code text-faint">model</span>{" "}
                          {formatUsd(w.cost_usd_micros)} ·{" "}
                          {formatTokens(w.input_tokens)}↑{" "}
                          {formatTokens(w.output_tokens)}↓ · {w.turns}{" "}
                          {w.turns === 1 ? "turn" : "turns"}
                        </span>
                        {(w.remote_env_minutes ?? 0) > 0 && (
                          <span className="text-muted">
                            <span className="font-code text-faint">
                              sandbox
                            </span>{" "}
                            {formatUsd(w.remote_env_cost_usd_micros ?? 0)} ·{" "}
                            {w.remote_env_minutes ?? 0}m
                          </span>
                        )}
                        {(w.resend_messages ?? 0) > 0 && (
                          <span className="text-muted">
                            <span className="font-code text-faint">email</span>{" "}
                            {w.resend_messages ?? 0}{" "}
                            {(w.resend_messages ?? 0) === 1 ? "msg" : "msgs"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {usage.daily_breakdown.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-baseline justify-between">
                  <p className="text-xs text-muted">Last 30 days</p>
                  <p className="font-code text-xs text-faint">
                    max {formatUsd(maxDaily)} / day
                  </p>
                </div>
                <div
                  className="flex h-20 items-end gap-0.5 rounded-md border border-edge bg-surface-raised/40 px-2 py-2"
                  data-testid="usage-breakdown"
                >
                  {usage.daily_breakdown.map((point) => {
                    const heightPct = Math.max(
                      point.cost_usd_micros > 0 ? 6 : 2,
                      Math.round((point.cost_usd_micros / maxDaily) * 100),
                    );
                    return (
                      <div
                        key={point.day}
                        className={`flex-1 rounded-sm ${point.cost_usd_micros > 0 ? "bg-accent/80" : "bg-edge"}`}
                        style={{ height: `${heightPct}%` }}
                        title={`${shortDay(point.day)}: ${formatUsd(point.cost_usd_micros)}`}
                      />
                    );
                  })}
                </div>
                {firstDay && lastDay && (
                  <div className="flex justify-between px-1 font-code text-xs text-faint">
                    <span>{shortDay(firstDay.day)}</span>
                    <span>{shortDay(lastDay.day)}</span>
                  </div>
                )}
              </div>
            )}

            {usage.top_model && (
              <p className="text-xs text-muted">
                Highest spend this month:{" "}
                <span className="font-code text-primary">{usage.top_model}</span>
              </p>
            )}
          </>
        )}
      </section>

      <section
        className="flex flex-col gap-4 border-t border-edge pt-6"
        data-testid="limits-section"
      >
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-primary">Limits</h2>
            <p className="mt-1 text-xs text-muted">
              Budget guardrails. A turn that would exceed a cap fails with a
              clean nudge in the channel — no tokens spent.
            </p>
          </div>
          {draft && (
            <button
              type="button"
              onClick={resetToDefaults}
              className="cursor-pointer text-xs text-muted transition-colors hover:text-primary"
              data-testid="limits-reset"
            >
              Reset to defaults
            </button>
          )}
        </div>

        {draft === null || limits === null ? (
          <p className="text-xs text-muted">Loading limits…</p>
        ) : (
          <form
            onSubmit={saveLimits}
            className="flex flex-col gap-3"
            data-testid="limits-form"
          >
            <label className="flex flex-col gap-1 text-xs text-muted">
              Max tokens per turn
              <div className="flex items-center rounded-md border border-edge bg-surface-raised focus-within:border-accent">
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={draft.per_turn_max_tokens}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      per_turn_max_tokens: Math.max(
                        1,
                        parseInt(e.target.value, 10) || 0,
                      ),
                    })
                  }
                  className="flex-1 bg-transparent px-3 py-2 font-code text-sm text-primary outline-none"
                  data-testid="limits-per-turn"
                />
                <span className="pr-3 text-xs text-faint">tokens</span>
              </div>
              <span className="text-xs text-faint">
                OpenRouter reserves this much against your balance per turn.
                2048 is sane for conversational replies; raise for long-form.
              </span>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-xs text-muted">
                Daily cap
                <div className="flex items-center rounded-md border border-edge bg-surface-raised focus-within:border-accent">
                  <span className="pl-3 text-xs text-faint">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={(draft.daily_cost_cap_micros / 1_000_000).toFixed(2)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        daily_cost_cap_micros: Math.max(
                          0,
                          Math.round(parseFloat(e.target.value || "0") * 1_000_000),
                        ),
                      })
                    }
                    className="flex-1 bg-transparent px-2 py-2 font-code text-sm text-primary outline-none"
                    data-testid="limits-daily"
                  />
                  <span className="pr-3 text-xs text-faint">/ day</span>
                </div>
              </label>

              <label className="flex flex-col gap-1 text-xs text-muted">
                Monthly cap
                <div className="flex items-center rounded-md border border-edge bg-surface-raised focus-within:border-accent">
                  <span className="pl-3 text-xs text-faint">$</span>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    value={(draft.monthly_cost_cap_micros / 1_000_000).toFixed(2)}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        monthly_cost_cap_micros: Math.max(
                          0,
                          Math.round(parseFloat(e.target.value || "0") * 1_000_000),
                        ),
                      })
                    }
                    className="flex-1 bg-transparent px-2 py-2 font-code text-sm text-primary outline-none"
                    data-testid="limits-monthly"
                  />
                  <span className="pr-3 text-xs text-faint">/ mo</span>
                </div>
              </label>
            </div>
            <p className="text-xs text-faint">
              Set either cap to 0 to disable that window entirely.
            </p>

            <label className="flex cursor-pointer items-start gap-2 rounded-md border border-edge bg-surface-raised/50 px-3 py-2 text-xs text-muted">
              <input
                type="checkbox"
                checked={draft.soft_limit}
                onChange={(e) => setDraft({ ...draft, soft_limit: e.target.checked })}
                className="mt-0.5 accent-current"
                data-testid="limits-soft"
              />
              <span className="flex flex-col gap-0.5">
                <span className="text-sm text-primary">Soft limit</span>
                <span>
                  When a cap is hit, post a nudge in the channel but still allow
                  the turn on request. Uncheck to refuse outright.
                </span>
              </span>
            </label>

            <div className="flex items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={busy || !dirty}
                className="cursor-pointer rounded-md bg-accent px-4 py-2 text-xs font-semibold text-[var(--wt-selection-text)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid="limits-save"
              >
                {busy ? "Saving…" : dirty ? "Save limits" : "Saved"}
              </button>
              {dirty && !busy && (
                <button
                  type="button"
                  onClick={() => setDraft(limits)}
                  className="cursor-pointer text-xs text-muted transition-colors hover:text-primary"
                  data-testid="limits-discard"
                >
                  Discard changes
                </button>
              )}
              {notice && (
                <span className="text-xs text-success" data-testid="limits-notice">
                  {notice}
                </span>
              )}
              {error && (
                <span className="text-xs text-danger" data-testid="limits-error">
                  {error}
                </span>
              )}
            </div>
          </form>
        )}
      </section>
    </>
  );
}
